import { action, computed, makeObservable, observable } from 'mobx';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import {
    CompetitionItemViewType,
    EventsCollectionList,
} from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { EventsCollectionState } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionState';
import { calculateRaceStatusFromEventItem } from 'src/domains/sportsbook/utils/filterRacesWithFinishState';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Common } from 'src/domains/common/Common';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventWithCompetitionIdType } from '../containers/sportMeetingsList/SportMeetingsList.state';

interface PropsType {
    sport: 'horseracing' | 'greyhoundracing';
    timezone?: string;
    racecardBuildIds: Array<number>;
    racecardCollection: 'next-off' | 'race-meetings' | number | null;
    racecardSelected: number | null;
}

const compareEvents = (a: EventListGroupEventItemType, b: EventListGroupEventItemType): number => {
    const time1 = a.startTime;
    const time2 = b.startTime;

    return time1 - time2;
};

interface EventsFilterOption {
    readonly isActive: boolean;
    readonly label: string;
    readonly key: 'next-off' | 'race-meetings' | number;
    readonly isArrowLeft?: boolean;
}

export type HorseRacingSortOrderType = 'by-price' | 'by-place' | null;

export class RaceCompetitionSetOrderState {
    @observable public sortOrder: HorseRacingSortOrderType = null;

    public constructor() {
        makeObservable(this);
    }

    @action.bound public setSortOrder = (sort: HorseRacingSortOrderType): void => {
        this.sortOrder = sort;
    };
}

export class RaceCompetitionState {
    private readonly models: ModelsState;
    public readonly starRouter: StarRouter;
    private readonly eventsCollectionState: EventsCollectionState;
    private readonly props: PropsType;

    public constructor(
        public readonly common: Common,
        models: ModelsState,
        starRouter: StarRouter,
        eventsCollectionState: EventsCollectionState,
        props: PropsType
    ) {
        this.models = models;
        this.starRouter = starRouter;
        this.eventsCollectionState = eventsCollectionState;
        this.props = props;
    }

    @computed public get racesCollection(): EventsCollectionList {
        return this.eventsCollectionState.getRacesForWeek(this.props.sport);
    }

    @computed public get currentCategory(): 'next-off' | 'race-meetings' | number {
        return this.props.racecardCollection ?? 'next-off';
    }

    @computed.struct public get racecardBuildModels(): Array<EventModel> {
        const eventsModel: Array<EventModel> = [];

        for (const eventId of this.props.racecardBuildIds) {
            const eventModel = this.models.getEvent(eventId);
            if (eventModel !== null) {
                eventsModel.push(eventModel);
            }
        }
        eventsModel.sort((a: EventModel, b: EventModel) => {
            return a.timeSettingsStartTimeUnixMs - b.timeSettingsStartTimeUnixMs;
        });

        return eventsModel;
    }

    @computed.struct public get racecardBuildIds(): Array<number> {
        return this.racecardBuildModels.map((item) => item.id);
    }

    @computed public get collectionEvents(): Array<EventWithCompetitionIdType> {
        const eventsWithCompetition: Array<EventWithCompetitionIdType> = [];
        for (const competition of this.racesCollection.competitionsRaw ?? []) {
            const competitionId = this.common.models.id.getCompetitionId(competition.id);
            for (const eventItem of competition.events) {
                eventsWithCompetition.push({
                    ...eventItem,
                    competitionId: competitionId,
                });
            }
        }

        return eventsWithCompetition;
    }

    @computed public get racecardBuildSmallEventModels(): Array<EventWithCompetitionIdType> {
        return this.collectionEvents.filter((item) => this.racecardBuildIds.includes(item.id));
    }

    @computed private get eventsBeforeSortSimple(): Array<EventWithCompetitionIdType> {
        const eventItems = this.collectionEvents;

        if (this.currentCategory === 'next-off') {
            return eventItems.filter((item) => calculateRaceStatusFromEventItem(item) !== 'RaceStatusFinished');
        }

        if (this.currentCategory === 'race-meetings') {
            return this.racecardBuildSmallEventModels;
        }

        return this.collectionEvents.filter((event) => event.competitionId.toOldId() === this.currentCategory) ?? [];
    }

    @computed private get eventsBeforeSort(): Array<EventWithCompetitionIdType> {
        return this.eventsBeforeSortSimple;
    }

    @computed public get events(): Array<EventWithCompetitionIdType> {
        return this.eventsBeforeSort.slice().sort(compareEvents);
    }

    @computed public get currentEventId(): number | null {
        const events = this.events;

        const eventId = this.props.racecardSelected;

        for (const event of events) {
            if (event.id === eventId) {
                return eventId;
            }
        }

        //get first
        for (const event of events) {
            return event.id;
        }

        return null;
    }

    @computed public get sport(): 'horseracing' | 'greyhoundracing' {
        return this.props.sport;
    }

    @computed private getCompetitionsForView(): Array<CompetitionItemViewType> {
        const competitionItems = this.racesCollection?.competitionForViewSortByName ?? [];

        const configComponents = ConfigComponents.get(this.common).config;

        if (configComponents.filterSortingMode.nextOff === 'name') {
            return competitionItems;
        }

        const byTime = this.nextStartTimeByCompetitionId;

        return competitionItems.slice().sort((a, b) => {
            const timeA = byTime.get(a.id);
            const timeB = byTime.get(b.id);

            if (timeA === undefined && timeB === undefined) {
                return a.name.localeCompare(b.name);
            }
            if (timeA === undefined) {
                return 1;
            }
            if (timeB === undefined) {
                return -1;
            }

            return timeA - timeB;
        });
    }

    @computed public get optionsForView(): Array<EventsFilterOption> {
        const currentCategory = this.currentCategory;
        const out: Array<EventsFilterOption> = [];
        const racecardBuildIds = this.racecardBuildIds;
        const languageState = LanguagesState.get(this.common);
        const { getTranslation } = languageState;

        if (racecardBuildIds.length > 0) {
            const isActive = currentCategory === 'race-meetings';

            out.push({
                isActive: isActive,
                label: getTranslation('events.racecard.filters.race-meetings', 'Racecard Builder'),
                key: 'race-meetings',
            });
        }

        const nextOffIsActive = currentCategory === 'next-off';

        out.push({
            isActive: nextOffIsActive,
            label: getTranslation('events.racecard.filters.next-off', 'Next Off'),
            key: 'next-off',
        });

        const competitions = this.getCompetitionsForView();

        for (const competitionItem of competitions) {
            const { id, name } = competitionItem;
            const isActive = currentCategory === id;

            out.push({
                isActive: isActive,
                label: name,
                key: id,
            });
        }

        return out;
    }

    @computed private get nextStartTimeByCompetitionId(): Map<number, number> {
        const map: Map<number, number> = new Map();

        const eventItems = this.racesCollection.eventItems ?? [];

        for (const item of eventItems) {
            if (calculateRaceStatusFromEventItem(item) === 'RaceStatusFinished') {
                continue;
            }

            const eventModel = this.models.getEvent(item.id);
            if (eventModel === null) {
                continue;
            }

            const competition = eventModel.competition2.getModel();
            const competitionId = competition?.id;

            if (competitionId === undefined) {
                continue;
            }

            const startTime = eventModel.timeSettingsStartTimeUnixMs;
            const prev = map.get(competitionId);

            if (prev === undefined || startTime < prev) {
                map.set(competitionId, startTime);
            }
        }

        return map;
    }

    @computed public get displayCompetition(): boolean {
        return this.currentCategory === 'next-off' || this.currentCategory === 'race-meetings';
    }

    @computed public get todayDate(): string {
        const date = DateTime.current().addDays(1).format('YYYY-MM-DD');
        return date;
    }

    @computed public get tomorrowDate(): string {
        const date = DateTime.current().addDays(1).format('YYYY-MM-DD');
        return date;
    }

    @computed public get afterTomorrowDate(): string {
        const date = DateTime.current().addDays(2).format('YYYY-MM-DD');
        return date;
    }

    @computed public get todayEvents(): Array<EventWithCompetitionIdType> {
        const out: EventWithCompetitionIdType[] = [];
        const races = this.events;

        const today = new Date(this.todayDate);

        for (const race of races) {
            const startTime = new Date(race.startTime);

            if (startTime <= today) {
                out.push(race);
            }
        }
        return out;
    }

    @computed public get tomorrowEvents(): Array<EventWithCompetitionIdType> {
        const out: EventWithCompetitionIdType[] = [];
        const races = this.events;
        const tomorrow = new Date(this.tomorrowDate);
        const afterTomorrow = new Date(this.afterTomorrowDate);

        for (const race of races) {
            const startTime = new Date(race.startTime);

            if (startTime > tomorrow && startTime < afterTomorrow) {
                out.push(race);
            }
        }
        return out;
    }

    @computed public get thisWeekEvents(): Array<EventWithCompetitionIdType> {
        const out: EventWithCompetitionIdType[] = [];
        const races = this.events;
        const afterTomorrow = new Date(this.afterTomorrowDate);

        for (const race of races) {
            const startTime = new Date(race.startTime);

            if (startTime >= afterTomorrow) {
                out.push(race);
            }
        }

        return out;
    }

    public onBackClick = (): void => {
        this.starRouter.redirectToRacingSport(this.props.sport);
    };
}
